import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';
import { BackIcon } from '../../../components/AppHeader';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CommentIcon from '@mui/icons-material/Comment';
import EditIcon from '@mui/icons-material/Edit';
import SpeedIcon from '@mui/icons-material/Speed';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import WarningIcon from '@mui/icons-material/Warning';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TimerIcon from '@mui/icons-material/Timer';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import Autocomplete from '@mui/material/Autocomplete';
import { compact } from 'lodash-es';
import { logAction } from '../../../utils/scoringLog';
import { useTranslation } from 'react-i18next';
import { getLatestScoresPerStage } from './ShooterSquadSelect';
import { getFlagSrc } from '../../../utils/scoring';
import { camelCase } from 'lodash-es';
import { submitEquipmentCheck, updateShooterComments, addChronoRequest, getChronos } from '../../../api';

function FlagId({ publicId, variant = 'body1' }) {
  const parts = (publicId || '').split('|');
  const displayId = parts[parts.length - 1];
  const countryCode = parts.length > 1 ? parts[0] : null;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      {countryCode && (
        <img alt={countryCode} src={getFlagSrc({ countryCode, size: 16 })} style={{ display: 'block' }} />
      )}
      <span>{displayId}</span>
    </Box>
  );
}

function buildTargetsString(targets, t, isRtl) {
  const numNoShoots = targets.noshoots === 'single' ? 1
    : targets.noshoots === 'several' ? 50
      : (targets['paper-no-shoots'] || 0) + (targets['metal-no-shoots'] || 0);
  const parts = compact([
    targets.paper ? t('stages.ipscTarget', { count: targets.paper }) : null,
    targets.minipaper ? t('stages.ipscMiniTarget', { count: targets.minipaper }) : null,
    targets.poppers ? t('stages.popper', { count: targets.poppers }) : null,
    targets.minipoppers ? t('stages.miniPopper', { count: targets.minipoppers }) : null,
    targets.plates ? t('stages.plate', { count: targets.plates }) : null,
    targets['pcc-ten-points-poppers'] ? t('stages.tenPointPopper', { count: targets['pcc-ten-points-poppers'] }) : null,
    targets['pcc-frangible'] ? t('stages.frangible', { count: targets['pcc-frangible'] }) : null,
    targets['pcc-ten-points-frangible'] ? t('stages.tenPointFrangible', { count: targets['pcc-ten-points-frangible'] }) : null,
    numNoShoots < 50 && targets['paper-no-shoots'] ? t('stages.paperNoShoot', { count: targets['paper-no-shoots'] }) : null,
    numNoShoots < 50 && targets['metal-no-shoots'] ? t('stages.metalNoShoot', { count: targets['metal-no-shoots'] }) : null,
    numNoShoots >= 50 ? t('stages.noShootsSeveral') : null,
  ]);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  const last = parts.pop();
  const m = isRtl ? '\u200F' : '';
  return parts.join(`${m}, ${m}`) + `${m}${t('common.lastSeparator')}${m}` + last;
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function Timer() {
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'grey.100', borderRadius: 2, px: 1.5, py: 0.5 }}>
      <TimerIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
      <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700, minWidth: 60, textAlign: 'center' }}>
        {formatTime(elapsed)}
      </Typography>
      <IconButton size="small" onClick={() => setRunning(!running)} color={running ? 'warning' : 'success'}>
        {running ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
      </IconButton>
      <IconButton size="small" onClick={() => { setRunning(false); setElapsed(0); }} disabled={elapsed === 0}>
        <ReplayIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

export default function ShooterList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stageIdx, squadId } = useParams();
  const [searchParams] = useSearchParams();
  const { matchId, match, stages, shooters, scores, speedTickets, addSpeedTicket, refreshShooters, refreshScores } = useOutletContext();

  // Refresh scores when returning to the shooter list
  useEffect(() => { refreshScores(); }, [refreshScores]);
  const stageIdxNum = parseInt(stageIdx, 10);
  const numStages = stages.length;
  const basePath = `/${matchId}/manage/scores`;

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuShooter, setMenuShooter] = useState(null);
  const [commentDialog, setCommentDialog] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [chronoDialog, setChronoDialog] = useState(null);
  const [chronoStations, setChronoStations] = useState(null);
  const [chronoFailed, setChronoFailed] = useState(false);
  const [chronoDone, setChronoDone] = useState(null);

  const handleMenuOpen = (e, shooter) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuShooter(shooter);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuShooter(null);
  };

  const handleComments = () => {
    setCommentDialog(menuShooter);
    setNewComment('');
    handleMenuClose();
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !commentDialog) return;
    const comments = [...(commentDialog.comments || []), {
      comment: newComment.trim(),
      name: 'RO',
      timestamp: Date.now(),
    }];
    try {
      await updateShooterComments(matchId, commentDialog.id, { comments });
      await refreshShooters();
      setNewComment('');
      setCommentDialog(null);
    } catch {}
  };

  const closeChrono = () => {
    setChronoDialog(null);
    setChronoStations(null);
    setChronoFailed(false);
  };

  const handleChrono = async () => {
    const shooter = menuShooter;
    handleMenuClose();
    try {
      const { data } = await getChronos(matchId);
      const stations = Array.isArray(data) ? data : data?.stations || [];
      setChronoStations(stations);
      setChronoFailed(false);
    } catch {
      // Report the failure rather than showing "no stations defined", which is a
      // different situation and hid the real error.
      setChronoStations(null);
      setChronoFailed(true);
    }
    setChronoDialog(shooter);
  };

  const handleChronoStation = async (stationIdx) => {
    if (!chronoDialog) return;
    try {
      await addChronoRequest(matchId, stationIdx, { shooterId: chronoDialog.id });
      setChronoDone({ shooter: chronoDialog.name, station: stationIdx + 1 });
      closeChrono();
    } catch {
      setChronoFailed(true);
    }
  };

  const handleEdit = () => {
    const shooter = menuShooter;
    handleMenuClose();
    navigate(`/${matchId}/manage/shooters?edit=${shooter.id}`);
  };

  const handleMarkAbsent = async () => {
    const shooter = menuShooter;
    handleMenuClose();
    try {
      await submitEquipmentCheck(matchId, {
        stageIdx: stageIdxNum,
        checks: { [shooter.id]: 'absent' },
      });
      await refreshShooters();
    } catch {}
  };

  const [arrivalDialog, setArrivalDialog] = useState(null);
  const [absentMenuAnchor, setAbsentMenuAnchor] = useState(null);
  const [absentMenuShooter, setAbsentMenuShooter] = useState(null);

  const handleAbsentMenuOpen = (e, shooter) => {
    e.stopPropagation();
    setAbsentMenuAnchor(e.currentTarget);
    setAbsentMenuShooter(shooter);
  };

  const handleAbsentMenuClose = () => {
    setAbsentMenuAnchor(null);
    setAbsentMenuShooter(null);
  };

  const handleMarkArrived = async () => {
    const shooter = arrivalDialog;
    setArrivalDialog(null);
    try {
      await submitEquipmentCheck(matchId, {
        stageIdx: stageIdxNum,
        checks: { [shooter.id]: 'ok' },
      });
      await refreshShooters();
    } catch {}
  };

  const [showArrivalErrorOptions, setShowArrivalErrorOptions] = useState(false);

  const handleEquipmentErrorAction = async (errorType) => {
    const shooter = arrivalDialog;
    setArrivalDialog(null);
    setShowArrivalErrorOptions(false);
    try {
      await submitEquipmentCheck(matchId, {
        stageIdx: stageIdxNum,
        checks: { [shooter.id]: errorType },
      });
      await refreshShooters();
    } catch {}
  };

  const squad = (match?.squads || []).find((s) => s.id === squadId);
  const allSquadShooters = shooters.filter((s) => s.squad === squadId && !s.cancelledParticipation);
  const splitParam = searchParams.get('split');
  const squadShooters = useMemo(() => {
    if (!splitParam) return allSquadShooters;
    const [part, total] = splitParam.split('-').map(Number);
    if (!part || !total || total < 2) return allSquadShooters;
    const size = Math.ceil(allSquadShooters.length / total);
    return allSquadShooters.slice((part - 1) * size, part * size);
  }, [allSquadShooters, splitParam]);

  // Compute DQ'd shooters across all stages
  const latestScores = getLatestScoresPerStage(scores, numStages);
  const shootersDqed = {};
  latestScores.forEach((stageScores, sIdx) => {
    stageScores.forEach((s) => {
      if (s.dq) shootersDqed[s.shooterId] = sIdx + 1;
    });
  });

  const stageScores = latestScores[stageIdxNum - 1] || [];

  // IROA/Round-robin ordering
  const orderedShooterIds = useMemo(() => {
    const paidShooters = squadShooters.filter((s) => match.payment === 'none' || s.paid);
    const unpaidShooters = squadShooters.filter((s) => match.payment !== 'none' && !s.paid);
    const ids = paidShooters.map((s) => s.id);
    const unpaidIds = unpaidShooters.map((s) => s.id);

    if (ids.length === 0) return unpaidIds;

    const offset = ((stageIdxNum - 1 - ((squad?.firstStage || 1) - 1) + numStages) % numStages) % ids.length;
    return [...ids.slice(offset), ...ids.slice(0, offset), ...unpaidIds];
  }, [squadShooters, stageIdxNum, squad, numStages, match.payment]);

  const shooterMap = {};
  shooters.forEach((s) => { shooterMap[s.id] = s; });

  // Categorize shooters
  const activeShooters = [];
  const absentShooters = [];
  const dqShooters = [];
  const unpaidShooters = [];

  orderedShooterIds.forEach((id) => {
    const shooter = shooterMap[id];
    if (!shooter) return;

    const equipCheck = (shooter.equipmentChecks || [])[stageIdxNum - 1];
    const dqStage = shootersDqed[id];

    if (match.payment !== 'none' && !shooter.paid) {
      unpaidShooters.push(shooter);
    } else if (dqStage && dqStage !== stageIdxNum) {
      dqShooters.push({ shooter, dqStage });
    } else if (equipCheck?.check === 'absent') {
      absentShooters.push(shooter);
    } else {
      activeShooters.push(shooter);
    }
  });

  const getScoreStatus = (shooterId) => {
    const score = stageScores.find((s) => s.shooterId === shooterId);
    if (!score) return null;
    if (score.dq) return 'dq';
    if (score.signature) return 'ok';
    return 'nosignature';
  };

  const handleShooterClick = (shooter) => {
    const status = getScoreStatus(shooter.id);
    const scorePath = `${basePath}/stage/${stageIdx}/squad/${squadId}/score-mode`;
    const qs = splitParam ? `?split=${splitParam}` : '';
    logAction('SHOOTER_SELECTED', { matchId, stageIdx: stageIdxNum, shooterId: shooter.id }, { name: shooter.name, status: status || 'new' });
    if (status === 'ok' || status === 'nosignature' || status === 'dq') {
      navigate(`${scorePath}/reviewing/${shooter.id}${qs}`);
    } else {
      navigate(`${scorePath}/scoring/${shooter.id}${qs}`);
    }
  };

  const [showStageDetails, setShowStageDetails] = useState(false);
  const [showSpeedTicketDialog, setShowSpeedTicketDialog] = useState(false);
  const stage = stages[stageIdxNum - 1];

  // Speed ticket shooters for this stage
  const stageSpeedTicketIds = (speedTickets[String(stageIdxNum)] || []);
  const speedTicketShooters = stageSpeedTicketIds
    .map((id) => shooterMap[id])
    .filter(Boolean)
    .filter((s) => {
      const dqStage = shootersDqed[s.id];
      return !dqStage || dqStage === stageIdxNum;
    });

  const displayShootersForTicket = shooters
    .filter((s) => !s.cancelledParticipation)
    .filter((s) => match.payment === 'none' || s.paid)
    .filter((s) => !squadShooters.some((sq) => sq.id === s.id))
    .filter((s) => !stageSpeedTicketIds.includes(s.id))
    .map((s) => ({ ...s, _displayName: s.name }));

  const statusColors = {
    dq: { bg: '#f44336', hover: '#d32f2f' },
    ok: { bg: '#009688', hover: '#00796b' },
    nosignature: { bg: '#009688', hover: '#00796b' },
  };

  return (
    <Box>
      {/* Timer */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Timer />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<BackIcon />}
          onClick={() => navigate(`${basePath}/stage/${stageIdx}/squad/${squadId}`)}>
          {t('common.back')}
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="subtitle1">
            {t('results.stageN', { n: stageIdx })} - {squad?.title || squadId}
            {' '}({activeShooters.filter((s) => getScoreStatus(s.id)).length}/{activeShooters.length})
          </Typography>
          <IconButton size="small" onClick={() => setShowStageDetails(true)}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Active shooters */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {activeShooters.map((shooter) => {
          const status = getScoreStatus(shooter.id);
          const hasComments = (shooter.comments || []).length > 0;
          const equipCheck = (shooter.equipmentChecks || [])[stageIdxNum - 1];
          const hasEquipError = equipCheck?.check === 'error-open' || equipCheck?.check === 'error-noscore';
          const colors = statusColors[status];
          return (
            <Box key={shooter.id} sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5 }}>
              <Button
                fullWidth variant="contained"
                onClick={() => handleShooterClick(shooter)}
                sx={{
                  justifyContent: 'flex-start', textTransform: 'none', py: 1.5, flex: 1, minWidth: 0,
                  ...(colors
                    ? { bgcolor: colors.bg, '&:hover': { bgcolor: colors.hover } }
                    : {}),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {status === 'ok' && <CheckCircleIcon sx={{ fontSize: 24 }} />}
                  {status === 'dq' && <Typography sx={{ fontWeight: 700, fontSize: 24, px: 0.5 }}>DQ</Typography>}
                  {status === 'nosignature' && <ErrorIcon sx={{ fontSize: 24 }} />}
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FlagId publicId={shooter.publicId} /> <Typography variant="body1">{shooter.name}</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {[shooter.division && t(`divisions.${camelCase(shooter.division)}`), shooter.category && t(`categories.${camelCase(shooter.category)}`), shooter['power-factor'] && t(`registration.${camelCase(shooter['power-factor'])}`)].filter(Boolean).join(' | ')}
                    </Typography>
                  </Box>
                  {hasEquipError && <WarningIcon sx={{ color: '#ff9800' }} />}
                  {hasComments && <CommentIcon sx={{ opacity: 0.7 }} />}
                </Box>
              </Button>
              <IconButton
                onClick={(e) => handleMenuOpen(e, shooter)}
                sx={{ alignSelf: 'center' }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      {/* DQ'd shooters */}
      {dqShooters.length > 0 && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">DQ</Typography>
          </Divider>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.5 }}>
            {dqShooters.map(({ shooter, dqStage }) => (
              <Box key={shooter.id} sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5 }}>
                <Button
                  fullWidth variant="contained"
                  disabled
                  sx={{
                    justifyContent: 'flex-start', textTransform: 'none', py: 1.5, flex: 1, minWidth: 0,
                    bgcolor: '#f44336', '&.Mui-disabled': { bgcolor: '#f44336', color: 'white' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 24, px: 0.5 }}>DQ</Typography>
                    <Box sx={{ flex: 1, textAlign: 'left' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FlagId publicId={shooter.publicId} /> <Typography variant="body1">{shooter.name}</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {[t('results.stageN', { n: dqStage }), shooter.division && t(`divisions.${camelCase(shooter.division)}`), shooter.category && t(`categories.${camelCase(shooter.category)}`), shooter['power-factor'] && t(`registration.${camelCase(shooter['power-factor'])}`)].filter(Boolean).join(' | ')}
                      </Typography>
                    </Box>
                  </Box>
                </Button>
                <IconButton disabled sx={{ alignSelf: 'center', opacity: 0 }}>
                  <MoreVertIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Absent shooters */}
      {absentShooters.length > 0 && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">{t('scoring.shooterNotHere')}</Typography>
          </Divider>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.5 }}>
            {absentShooters.map((shooter) => (
              <Box key={shooter.id} sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5 }}>
                <Button
                  fullWidth variant="contained"
                  onClick={(e) => handleAbsentMenuOpen(e, shooter)}
                  sx={{
                    justifyContent: 'flex-start', textTransform: 'none', py: 1.5, flex: 1, minWidth: 0,
                    bgcolor: 'grey.400', '&:hover': { bgcolor: 'grey.500' }, color: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <PersonOffIcon sx={{ fontSize: 24 }} />
                    <Box sx={{ flex: 1, textAlign: 'left' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FlagId publicId={shooter.publicId} /> <Typography variant="body1">{shooter.name}</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {[shooter.division && t(`divisions.${camelCase(shooter.division)}`), shooter.category && t(`categories.${camelCase(shooter.category)}`), shooter['power-factor'] && t(`registration.${camelCase(shooter['power-factor'])}`)].filter(Boolean).join(' | ')}
                      </Typography>
                    </Box>
                  </Box>
                </Button>
                <IconButton
                  onClick={(e) => handleAbsentMenuOpen(e, shooter)}
                  sx={{ alignSelf: 'center' }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Unpaid shooters */}
      {unpaidShooters.length > 0 && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">{t('manage.unpaid')}</Typography>
          </Divider>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.5 }}>
            {unpaidShooters.map((shooter) => (
              <Box key={shooter.id} sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5 }}>
                <Button
                  fullWidth variant="contained"
                  disabled
                  sx={{
                    justifyContent: 'flex-start', textTransform: 'none', py: 1.5, flex: 1, minWidth: 0,
                    '&.Mui-disabled': { bgcolor: 'grey.400', color: 'white' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Box sx={{ flex: 1, textAlign: 'left' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FlagId publicId={shooter.publicId} /> <Typography variant="body1">{shooter.name}</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {[shooter.division && t(`divisions.${camelCase(shooter.division)}`), shooter.category && t(`categories.${camelCase(shooter.category)}`), shooter['power-factor'] && t(`registration.${camelCase(shooter['power-factor'])}`)].filter(Boolean).join(' | ')}
                      </Typography>
                    </Box>
                  </Box>
                </Button>
                <IconButton disabled sx={{ alignSelf: 'center', opacity: 0 }}>
                  <MoreVertIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Speed Tickets */}
      {speedTicketShooters.length > 0 && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">{t('scoring.speedTickets')}</Typography>
          </Divider>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {speedTicketShooters.map((shooter) => {
              const status = getScoreStatus(shooter.id);
              const colors = statusColors[status];
              return (
                <Button
                  key={shooter.id} fullWidth variant="contained"
                  onClick={() => handleShooterClick(shooter)}
                  sx={{
                    justifyContent: 'flex-start', textTransform: 'none', py: 1.5,
                    ...(colors
                      ? { bgcolor: colors.bg, '&:hover': { bgcolor: colors.hover } }
                      : {}),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <RocketLaunchIcon sx={{ opacity: 0.8 }} />
                    {status === 'ok' && <CheckCircleIcon sx={{ fontSize: 24 }} />}
                    {status === 'dq' && <Typography sx={{ fontWeight: 700, fontSize: 24, px: 0.5 }}>DQ</Typography>}
                    {status === 'nosignature' && <ErrorIcon sx={{ fontSize: 24 }} />}
                    <Box sx={{ flex: 1, textAlign: 'left' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FlagId publicId={shooter.publicId} /> <Typography variant="body1">{shooter.name}</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {[shooter.division && t(`divisions.${camelCase(shooter.division)}`), shooter.category && t(`categories.${camelCase(shooter.category)}`), shooter['power-factor'] && t(`registration.${camelCase(shooter['power-factor'])}`)].filter(Boolean).join(' | ')}
                      </Typography>
                    </Box>
                  </Box>
                </Button>
              );
            })}
          </Box>
        </>
      )}

      {/* Add Speed Ticket button */}
      <Box sx={{ mt: 2 }}>
        <Button
          fullWidth variant="contained" color="secondary"
          onClick={() => setShowSpeedTicketDialog(true)}
          sx={{ py: 1.5, textTransform: 'none' }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {t('scoring.addSpeedTicket')}
          </Typography>
        </Button>
      </Box>

      {/* Speed Ticket Dialog */}
      <Dialog open={showSpeedTicketDialog} onClose={() => setShowSpeedTicketDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('scoring.addSpeedTicket')}
          <IconButton onClick={() => setShowSpeedTicketDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('scoring.pickShooter')}
          </Typography>
          <Autocomplete
            options={displayShootersForTicket}
            getOptionLabel={(s) => `${s.publicId} ${s._displayName || s.name}`}
            onChange={(_, shooter) => {
              if (!shooter) return;
              addSpeedTicket(stageIdxNum, shooter.id);
              setShowSpeedTicketDialog(false);
            }}
            renderOption={(props, s) => {
              const { key, ...rest } = props;
              return <li key={key} {...rest}><FlagId publicId={s.publicId} />&nbsp;- {s._displayName || s.name}</li>;
            }}
            renderInput={(params) => <TextField {...params} autoFocus />}
            blurOnSelect
            value={null}
          />
        </DialogContent>
      </Dialog>

      {/* Shooter context menu */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleMenuClose}>
        <MenuItem onClick={handleComments}>
          <ListItemIcon><CommentIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('registration.comments')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleChrono}>
          <ListItemIcon><SpeedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('scoring.addToChrono')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('common.edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMarkAbsent}>
          <ListItemIcon><PersonOffIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('scoring.markAbsent')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Comments dialog */}
      <Dialog open={!!commentDialog} onClose={() => setCommentDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('registration.comments')} - {commentDialog?.name}</DialogTitle>
        <DialogContent>
          {(commentDialog?.comments || []).length > 0 ? (
            <Box sx={{ mb: 2 }}>
              {commentDialog.comments.map((c, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                  &bull; {c.comment} <Typography component="span" variant="caption" color="text.secondary">({c.name})</Typography>
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              - {t('common.none')} -
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small" fullWidth
              placeholder={t('registration.comments')}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <Button size="small" variant="outlined" onClick={handleAddComment}>+</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialog(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Chrono station dialog */}
      <Dialog open={!!chronoDialog} onClose={closeChrono} maxWidth="xs" fullWidth>
        <DialogTitle>{t('chrono.newRequestTitle')} - {chronoDialog?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('chrono.newRequestDesc1')}</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('chrono.newRequestDesc2')}</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>{t('chrono.newRequestDesc3')}</Typography>
          {chronoFailed && (
            <Typography variant="body2" color="error">{t('common.error')}</Typography>
          )}
          {!chronoFailed && chronoStations && chronoStations.length === 0 && (
            <Typography variant="body2" color="text.secondary">{t('scoring.noChronoStations')}</Typography>
          )}
          {!chronoFailed && chronoStations && chronoStations.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {chronoStations.map((_, idx) => (
                <Button key={idx} variant="contained" fullWidth onClick={() => handleChronoStation(idx)}>
                  {t('scoring.chronoStation', { n: idx + 1 })}
                </Button>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeChrono}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Chrono done dialog */}
      <Dialog open={!!chronoDone} onClose={() => setChronoDone(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('chrono.newRequestDoneTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('chrono.newRequestDone1', { shooter: chronoDone?.shooter })}</Typography>
          <Typography variant="body2">{t('chrono.newRequestDone2', { station: chronoDone?.station })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChronoDone(null)}>{t('common.ok')}</Button>
        </DialogActions>
      </Dialog>

      {/* Stage Details Dialog */}
      <Dialog open={showStageDetails} onClose={() => setShowStageDetails(false)} maxWidth="md" fullWidth>
        {stage && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {t('results.stageN', { n: stageIdx })}{stage.title ? ` - ${stage.title}` : ''}
              <IconButton onClick={() => setShowStageDetails(false)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {stage.img && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <Box
                    component="img"
                    src={stage.img}
                    alt={`Stage ${stageIdx}`}
                    sx={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }}
                  />
                  <Box>
                    <Button
                      component="a" href={stage.img} target="_blank" rel="noopener noreferrer"
                      size="small" startIcon={<OpenInNewIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('stages.openImage')}
                    </Button>
                  </Box>
                </Box>
              )}
              <StageDetailsTable stage={stage} stageIdx={stageIdxNum} />
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Absent shooter menu */}
      <Menu anchorEl={absentMenuAnchor} open={!!absentMenuAnchor} onClose={handleAbsentMenuClose}>
        <MenuItem onClick={() => { setArrivalDialog(absentMenuShooter); handleAbsentMenuClose(); }}>
          <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('scoring.approveArrival', 'Approve Arrival')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Arrival / equipment check dialog */}
      <Dialog open={!!arrivalDialog} onClose={() => { setArrivalDialog(null); setShowArrivalErrorOptions(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>{t('scoring.markArrived', 'Shooter Arrived')}</DialogTitle>
        <DialogContent>
          {arrivalDialog && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, mb: 2 }}>
                <FlagId publicId={arrivalDialog.publicId} /> {arrivalDialog.name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('registration.division')}: {arrivalDialog.division ? t(`divisions.${camelCase(arrivalDialog.division)}`) : '-'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('registration.category')}: {arrivalDialog.category ? t(`categories.${camelCase(arrivalDialog.category)}`) : '-'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('registration.powerFactor')}: {arrivalDialog['power-factor'] ? t(`registration.${camelCase(arrivalDialog['power-factor'])}`) : '-'}
              </Typography>
              {showArrivalErrorOptions && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button fullWidth variant="contained" color="warning"
                    onClick={() => handleEquipmentErrorAction('error-open')}>
                    {t('scoring.shooterEquipmentFailedMoveToOpen')}
                  </Button>
                  <Button fullWidth variant="contained" color="error"
                    onClick={() => handleEquipmentErrorAction('error-noscore')}>
                    {t('scoring.shooterEquipmentFailedMoveToNoScore')}
                  </Button>
                  <Button fullWidth variant="outlined" size="small"
                    onClick={() => setShowArrivalErrorOptions(false)}>
                    {t('common.cancel')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        {!showArrivalErrorOptions && (
          <DialogActions>
            <Button color="error" onClick={() => setShowArrivalErrorOptions(true)}>{t('scoring.equipmentError', 'Equipment Error')}</Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={() => { setArrivalDialog(null); setShowArrivalErrorOptions(false); }}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={handleMarkArrived}>{t('scoring.confirmArrived', 'Confirm Arrival')}</Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
}

function StageDetailsTable({ stage, stageIdx }) {
  const { t, i18n } = useTranslation();
  const { targets, rounds, startPosition, gunCondition, pccCondition, start, procedure, end, comments, bonusTargets } = stage;
  const targetsStr = buildTargetsString(targets, t, i18n.dir() === 'rtl');
  const tenPointsExtra = ((targets['pcc-ten-points-poppers'] || 0) + (targets['pcc-ten-points-frangible'] || 0)) * 5;
  const paperCount = (targets.paper || 0) + (targets.minipaper || 0);
  const steelCount = (targets.poppers || 0) + (targets.minipoppers || 0) + (targets.plates || 0)
    + (targets['pcc-ten-points-poppers'] || 0) + (targets['pcc-frangible'] || 0) + (targets['pcc-ten-points-frangible'] || 0);
  let roundsNote = '';
  if (paperCount > 0 && rounds === paperCount + steelCount) {
    roundsNote = t('stages.shotPerPaper1');
  } else if (paperCount > 0 && rounds === paperCount * 3 + steelCount) {
    roundsNote = t('stages.shotPerPaper3');
  }

  const rows = [
    { label: t('stages.targets'), value: targetsStr },
    { label: t('stages.rounds'), value: t('stages.roundsAndPoints', { rounds, points: rounds * 5 + tenPointsExtra }), note: roundsNote },
    bonusTargets?.length > 0 && { label: t('stages.bonusTargets'), value: bonusTargets.map((s) => `T${s + 1}`).join(', ') },
    startPosition && { label: t('stages.startPosition'), value: startPosition },
    gunCondition && { label: t('stages.gunCondition'), value: gunCondition },
    pccCondition && { label: t('stages.pccCondition'), value: pccCondition },
    start && { label: t('stages.start'), value: start },
    procedure && { label: t('stages.procedure'), value: procedure },
    end && { label: t('stages.end'), value: end },
    comments && { label: t('stages.comments'), value: comments },
  ].filter(Boolean);

  return (
    <Table dir={i18n.dir()} size="small" sx={{ '& td': { border: '1px solid rgba(0,0,0,0.2)', p: 1, textAlign: 'start' } }}>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            <TableCell sx={{ fontWeight: 'bold', width: 150 }}>{row.label}</TableCell>
            <TableCell>
              {row.value}
              {row.note && <Typography variant="caption" display="block">{row.note}</Typography>}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
